<template>
  <div class="creativeUnitTable">
    <slot-table id="creativeUnitTable"
                class="creativeUnitTable__table table-bordered scrollTable"
                :columns="columnData"
                :data="tableData"
                :editable="true"
                :sortable="true"
                :allowedSortColumns="tableAllowedSortColumns"
                :activeSortData="tableActiveSortData"
                @toggleCheckboxes="toggleParentCheckboxes"
                @onAddSort="addSort"
                @dispatchSortEvent="dispatchSortEvent"
    >

      <template slot="allCheckboxes" slot-scope="props">
            <span><checkbox :id="`checkbox_${props.item.id}`" @selectCheckboxes="groupSelectCheckboxes($event)" v-model="props.item.selected"
                            :value="props.item.selected"/></span>
      </template>

      <template slot="creativeActions" slot-scope="props">

        <a v-if="props.item.previewUrl" title="Preview Unit" class="btn btn-primary btn-xs" :data-previewId="props.item.id" @click.prevent="launchPreview" href="#"
           target="_blank">
          <i class="fa fa-eye"></i>
        </a>
        <router-link title="Edit Unit" v-if="!props.item.isNew && !props.item.readOnly" class="btn btn-info btn-xs" :to="{ path: `/creatives/${creative.hash_id}/single/${props.item.id}` }">
          <i class="fa fa-th" aria-hidden="true"></i>
        </router-link>
        <button-cta title="Copy Unit" data-modalType="copyCreativeUnit" :data-modalID="props.item.id"
                    :onClick="copyRow" class="btn btn-success btn-xs" :data-modalField="props.item.locale">
          <i class="fa fa-clone" aria-hidden="true"></i>
        </button-cta>
        <slot v-if="shouldShowCallToActions(props.item, true)">
          <button-cta :title="props.item.editing ? 'Save Unit' : 'Edit Unit'" :onClick="editRow" :data-rowId="props.item.id" :data-isNew="props.item.isNew"
                      :class="`btn btn-${props.item.editing ? 'success' : 'warning'} btn-xs`">
            <i :class="`fa fa-${props.item.editing ? 'check' : 'edit'}`" aria-hidden="true"></i>
          </button-cta>
          <button-cta :title="props.item.editing ? 'Cancel Changes' : 'Delete Unit'" data-modalType="deleteCreativeUnit" :data-modalID="props.item.id"
                      :onClick="props.item.editing ? cancelRow : deleteRow" class="btn btn-danger btn-xs">
            <i :class="`fa fa-${props.item.editing ? 'times' : 'trash'}`" aria-hidden="true"></i>
          </button-cta>
        </slot>
      </template>

      <template slot="locale" slot-scope="props">
        <span v-if="props.item.editing"><field :value="props.item.locale"
                                                  v-model="props.item.locale"/></span>
        <span v-else>{{ props.item.locale }}</span>
      </template>

      <template slot="width" slot-scope="props">
        <span v-if="props.item.editing"><field :value="props.item.width"
                                                  v-model="props.item.width"/></span>
        <span v-else>{{ props.item.width }}</span>
      </template>

      <template slot="height" slot-scope="props">
        <span v-if="props.item.editing"><field :value="props.item.height"
                                                  v-model="props.item.height"/></span>
        <span v-else>{{ props.item.height }}</span>
      </template>
      <template slot="render_video_duration" slot-scope="props">
        <span v-if="props.item.editing"><field :value="props.item.render_video_duration"
                                               :type="'number'"
                                               v-model="props.item.render_video_duration"/></span>
        <span v-else>{{ props.item.render_video_duration }}</span>
      </template>

      <template slot="render_video_fps" slot-scope="props">
            <span>
              <span v-if="props.item.editing">
                <dropdown :options="fpsOptions" v-model="props.item.render_video_fps" :current="props.item.render_video_fps"/>
              </span>
              <span v-else>{{ props.item.render_video_fps }}</span>
            </span>
      </template>
      <template slot="render_video_format" slot-scope="props">
            <span>
              <span v-if="props.item.editing">
                <dropdown :options="videoFormatOptions" v-model="props.item.render_video_format" :current="props.item.render_video_format"/>
              </span>
              <span v-else>{{ props.item.render_video_format }}</span>
            </span>
      </template>
      <template slot="date_created" slot-scope="props">
        <span>{{ props.item.date_created.value | formatDate }}</span>
        <span>{{ props.item.date_created.value | formatTime }}</span>
      </template>

      <template slot="status" slot-scope="props">
        <span v-if="props.item.status === 'error'">
          <tooltip :text="props.item.status_message" :position="getPropsIndex(props.item)">
            <span
                :class="statusCss(props.item.status)">
              <i class="fa fa-exclamation-triangle"></i>
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else-if="props.item.status === 'under_review'">
          <tooltip :text="getReviewLinkName(props.item)" :position="getPropsIndex(props.item)">
            <span
                :class="statusCss(props.item.status)">
              {{ props.item.status }}
            </span>
          </tooltip>
        </span>
        <span v-else>
          <span :class="statusCss(props.item.status)">
            {{ props.item.status.replace(/_/g, ' ')}}
          </span>
        </span>
      </template>

      <template slot="master" slot-scope="props">
            <span>
              <span v-if="props.item.editing">
                <dropdown :options="masterOptions" v-model="props.item.master" :current="props.item.master"/>
              </span>
              <span v-else>{{ props.item.master }}</span>
            </span>
      </template>

      <template v-for="(field, index) in tableFields" :slot="field" slot-scope="props">
            <span :key="index">
              <span v-if="tableFieldTypes[index] === 'image'" class="imageHolder">
                <span v-if="props.item.editing"
                      class="creativeUnitTable__imageField"
                      @click="updateImage"
                      data-modalType="assetLibrary"
                      :data-modalID="props.item.id"
                      :data-modalField="field"
                      :data-modalFieldType="tableFieldTypes[index]"
                >
                  <span v-if="!props.item.fields[field]">
                    <span class="item"><i aria-hidden="true" class="fa fa-plus"></i> Add Image</span>
                  </span>
                  <span v-else>
                    <span class="thumb item"><img :src="thumbPath( props.item.fields[field] )" onerror="this.style.display='none'"/></span>
                    <span class="item"><i>{{ props.item.fields[field] }}</i></span>
                  </span>
                </span>
                  <span v-else>
                    <span class="thumb item">
                     <img :src="thumbPath( props.item.fields[field] )" onerror="this.style.display='none'"/>
                      </span>
                  <span class="item">
                    <i>{{ props.item.fields[field] }}</i>
                    </span>
                  </span>
              </span>
              <span v-else-if="tableFieldTypes[index] === 'video'" class="imageHolder">
                <span v-if="props.item.editing"
                      class="creativeUnitTable__imageField"
                      @click="updateImage"
                      data-modalType="assetLibrary"
                      :data-modalID="props.item.id"
                      :data-modalField="field"
                      :data-modalFieldType="tableFieldTypes[index]"
                >
                  <span v-if="!props.item.fields[field]">
                    <span class="item"><i aria-hidden="true" class="fa fa-plus"></i> Add Video</span>
                  </span>
                  <span v-else>
                    <span class="thumb item"><img :src="videoThumbPath( props.item.fields[field] )" onerror="this.style.display='none'"/></span>
                    <span class="item"><i>{{ props.item.fields[field] }}</i></span>
                  </span>
                </span>
                  <span v-else>
                    <span class="thumb item">
                     <img :src="videoThumbPath( props.item.fields[field] )" onerror="this.style.display='none'"/>
                      </span>
                  <span class="item">
                    <i>{{ props.item.fields[field] }}</i>
                    </span>
                  </span>
              </span>
              <span v-else-if="tableFieldTypes[index] === 'yesno'">
                <span v-if="props.item.editing">
                  <dropdown :options="yesNoOptions" v-model="props.item.fields[field]" :current="props.item.fields[field]"/>
                </span>
                <span v-else>
                  <slot v-if="table.yesNoOptions[props.item.fields[field]]">
                    {{ table.yesNoOptions[props.item.fields[field]]['name'] }}
                  </slot>
                </span>
              </span>
              <span v-else-if="tableFieldTypes[index] === 'constrain' || tableFieldTypes[index] === 'boolean' || tableFieldTypes[index] === 'keepaspect'">
                <span v-if="props.item.editing">
                  <dropdown :options="booleanOptions" v-model="props.item.fields[field]" :current="props.item.fields[field]"/>
                </span>
                <span v-else>
                  <slot v-if="table.booleanOptions[props.item.fields[field]]">
                    {{ table.booleanOptions[props.item.fields[field]]['name'] }}
                  </slot>
                </span>
              </span>
              <span v-else-if="tableFieldTypes[index] === 'alignment'">
                <span v-if="props.item.editing">
                  <dropdown :options="alignmentOptions" v-model="props.item.fields[field]" :current="props.item.fields[field]"/>
                </span>
                <span v-else>
                  <slot v-if="table.alignmentOptions[props.item.fields[field]]">
                    {{ table.alignmentOptions[props.item.fields[field]]['name'] }}
                  </slot>
                </span>
              </span>
              <span v-else-if="tableFieldTypes[index] === 'color'">
                <span v-if="props.item.editing">
                  <color-picker v-model="props.item.fields[field]" :color="props.item.fields[field]" :creativeType='creative.type'/>
                </span>
                <span v-else>
                  <slot v-if="props.item.fields[field]">
                    <div class="colorPicker">{{ props.item.fields[field] }}<span :style="{ backgroundColor: props.item.fields[field] }">&nbsp;</span></div>
                  </slot>
                  <slot v-else>
                    No color has been set.
                  </slot>
                </span>
              </span>
              <span v-else>
                <span v-if="props.item.editing"><text-tag
                    :value="props.item.fields[field]" v-model="props.item.fields[field]"/></span>
                <span v-else>{{ formatTextString(props.item.fields[field]) }}</span>
              </span>
            </span>
      </template>
    </slot-table>
  </div>
</template>

<script>
import SlotTable from '@/components/Table/table'
import ButtonCta from '@/components/Button/button'
import Heading from '@/components/Heading/heading'
import Field from '@/components/Form/Field/field'
import SwitchButton from '@/components/SwitchButton/switch-button'
import Checkbox from '@/components/Form/Checkbox/checkbox'
import TextTag from '@/components/Form/TextTag/text-tag'
import Dropdown from '@/components/Form/Dropdown/dropdown'
import Tooltip from '@/components/Tooltip/tooltip'
import ColorPicker from '@/components/ColorPicker/color-picker'

import ModalAssetLibrary from '@/components/Modal/assets/modal-asset-library'

import {modalMixin} from '@/mixins/modalMixin'

import { API_URL, MEDIA_URL } from '@/config'

import {findIndex, propEq} from 'ramda'

export default {
  name: 'CreativeUnitTable',
  components: {
    SlotTable,
    ButtonCta,
    Field,
    SwitchButton,
    Checkbox,
    Heading,
    ModalAssetLibrary,
    TextTag,
    Dropdown,
    Tooltip,
    ColorPicker
  },
  mixins: [modalMixin],
  props: {
    tableData: Array,
    tableFields: Array,
    tableFieldTypes: Array,
    tableSortList: Array,
    columnData: Object,
    mastersData: Array,
    creative: Object,
    tableAllowedSortColumns: { type: Array, default: null },
    tableActiveSortData: { type: Array, default: null }
  },
  data: () => ({
    table: {
      columns: {
        is_editable: 'allCheckboxes',
        name: 'Name',
        type: 'Type',
        limit: 'Limit',
        is_hard_limit: 'Hard&nbsp;Limit',
        defaultValue: 'Default Value'
      },
      statusKeys: {
        rendered: 'success',
        under_review: 'warning',
        edited: 'info',
        error: 'danger',
        queued: 'warning'
      },
      booleanOptions: {
        0: {
          name: 'FALSE',
          value: 0
        },
        1: {
          name: 'TRUE',
          value: 1
        }
      },
      yesNoOptions: {
        'no': {
          name: 'no',
          value: 'no'
        },
        'yes': {
          name: 'yes',
          value: 'yes'
        }
      },
      alignmentOptions: {
        'left': {
          name: 'left',
          value: 'left'
        },
        'center': {
          name: 'center',
          value: 'center'
        },
        'right': {
          name: 'right',
          value: 'right'
        }
      },
      videoFormatOptions: {
        'mp4': {
          name: 'mp4',
          value: 'mp4'
        },
        'gif': {
          name: 'gif',
          value: 'gif'
        }
      },
      fpsOptions: {
        '12': {
          name: '12',
          value: '12'
        },
        '15': {
          name: '15',
          value: '15'
        },
        '24': {
          name: '24',
          value: '24'
        },
        '29': {
          name: '29',
          value: '29'
        },
        '30': {
          name: '30',
          value: '30'
        },
        '50': {
          name: '50',
          value: '50'
        },
        '60': {
          name: '60',
          value: '60'
        }
      }
    },
    lastChecked: null
  }),
  computed: {
    masterOptions () {
      const optionsArray = []
      this.mastersData.forEach((item) => {
        const option = {
          name: item,
          value: item
        }
        optionsArray.push(option)
      })
      return optionsArray
    },
    booleanOptions () {
      const optionsArray = []
      for (var key in this.table.booleanOptions) {
        const option = {
          name: this.table.booleanOptions[key].name,
          value: this.table.booleanOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    yesNoOptions () {
      const optionsArray = []
      for (var key in this.table.yesNoOptions) {
        const option = {
          name: this.table.yesNoOptions[key].name,
          value: this.table.yesNoOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    alignmentOptions () {
      const optionsArray = []
      for (var key in this.table.alignmentOptions) {
        const option = {
          name: this.table.alignmentOptions[key].name,
          value: this.table.alignmentOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    videoFormatOptions () {
      const optionsArray = []
      for (var key in this.table.videoFormatOptions) {
        const option = {
          name: this.table.videoFormatOptions[key].name,
          value: this.table.videoFormatOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    },
    fpsOptions () {
      const optionsArray = []
      for (var key in this.table.fpsOptions) {
        const option = {
          name: this.table.fpsOptions[key].name,
          value: this.table.fpsOptions[key].value
        }
        optionsArray.push(option)
      }
      return optionsArray
    }
  },
  mounted () {
    this.newData = this.tableData
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    shouldShowCallToActions (item, validateStateQueued) {
      if (item.readOnly) return false
      if (validateStateQueued && item.status === 'queued') return false
      return true
    },
    toggleParentCheckboxes (toggle) {
      this.$emit('onToggleParentCheckboxes', toggle)
    },
    thumbPath (filename) {
      var thumbpath = MEDIA_URL + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_assets/' + filename + '?ts=' + Math.round(+new Date() / 1000)
      return thumbpath
    },
    videoThumbPath (filename) {
      var thumbpath = MEDIA_URL + this.creative.campaign.client.slug.toLowerCase() + '/' + this.creative.campaign.hash_id + '/' + this.creative.hash_id + '/_thumbnails/' + filename + '_thumb.jpeg' + '?ts=' + Math.round(+new Date() / 1000)
      return thumbpath
    },
    previewUrl (previewUrl) {
      return API_URL + previewUrl
    },
    editRow (e) {
      this.$emit('onEditRow', e)
    },
    deleteRow (e) {
      this.$emit('onDeleteRow', e)
    },
    copyRow (e) {
      this.$emit('onCopyRow', e)
    },
    cancelRow (e) {
      console.log('Child cancel row.')
      this.$emit('onCancelRow', e)
    },
    launchPreview (e) {
      this.$emit('onPreview', e)
    },
    statusCss (status) {
      let labelType = 'default'
      if (status !== 'new') {
        labelType = this.table.statusKeys[status]
      }
      return `label label-${labelType} text-center text-capitalize`
    },
    updateImage (e) {
      this.$emit('onUpdateImage', e)
    },
    formatTextString (string) {
      return string ? string.replace(/\\n/g, '\n') : ''
    },
    handleResize: function (event) {
      // On resize make the scroll area size the same as the table area
    },
    addSort: function (field) {
      this.$emit('onSortTableData', field)
    },
    dispatchSortEvent: function (sortData) {
      this.$emit('onDispatchSortEvent', sortData)
    },
    getReviewLinkName (item) {
      let value = ''
      if (item.review_link_snapshot) {
        value = item.review_link_snapshot.name
      }
      return value
    },
    getPropsIndex (item) {
      let i = findIndex(propEq('id', item.id))(this.tableData)
      if (i === 0) {
        return 'bottom'
      } else {
        return 'right'
      }
    },
    groupSelectCheckboxes (e) {
      if (this.lastChecked && e.shiftKey) {
        // Get indexes of the two clicked checkboxes
        const previousId = this.lastChecked.id.replace('checkbox_', '')
        let i = findIndex(propEq('id', previousId))(this.tableData)
        const currentId = e.target.id.replace('checkbox_', '')
        let j = findIndex(propEq('id', currentId))(this.tableData)

        let checkOrUncheck = e.target.checked

        // up or down selection
        let low = i
        let high = j
        if (i > j) {
          low = j
          high = i
        }

        // Set checked state
        for (let c = 0; c < this.tableData.length; c++) {
          if (low <= c && c <= high) {
            this.tableData[c].selected = checkOrUncheck
          }
        }
        this.lastChecked = null
      }
      this.lastChecked = e.target // Store this input as the last clicked
    }
  }
}
</script>

<style lang="scss">
    $defaultTdWidth: 100px;
    $headerHeight: 260px; //Height of header area above table this is not dynamic, its hard coded for now.
    $cellWidths: 250px;
    $scrollHeight: 550px; //Min hiehgt to get htis working
    .creativeUnitTable {
      overflow-x: auto;
      overflow-y: hidden;
      max-width: 100%;
      margin: 0px 0 10px;
      height:calc(80vh - #{$headerHeight});
      background-color:#FFF;
      &__table {
        margin-bottom: 0;
      }
      .form-control {
        background-color: transparent;
      }
      & .colorPicker {
        border:1px solid #ccc;
        background-color:#eee;
        border-radius: 5px;
        width:231px;
        padding:4px;
        text-align: left;
        margin: 0 auto;
        padding: 10px 12px;
        font-size: 14px;
        font-weight: normal;
        line-height: 1;
        & span {
          border: 1px solid #ccc;
          height: 16px;
          width: 16px;
          display: inline-block;
          float: right;
        }
      }
      &__imageField {
        cursor: pointer;
      }
      img {
            max-width: 50px;
            max-height: 50px;
      }
      .imageHolder{
        display: flex;
        min-height: 50px;
        align-items: center;
        justify-content: center;
        & .item{
          display: inline-flex;
        }
        & .thumb{
          justify-content: center;
          border:1px solid black;
          display: contents;
         & img{
            max-height:50px;
            max-width:50px;
            padding:0;
          }
        }
      }
    }

  /* Start of external code used for fixed headsers */
  /* define height and width of scrollable area. Add 16px to width for scrollbar          */
  /* Reset overflow value to hidden for all non-IE browsers. */
  html>body .creativeUnitTable div.tableContainer {
    overflow: hidden;
  }

  /* define width of table. IE browsers only                 */
  .creativeUnitTable div.tableContainer table {
    float: left;
  }

  /* set table header to a fixed position. WinIE 6.x only                                       */
  /* In WinIE 6.x, any element with a position property set to relative and is a child of       */
  /* an element that has an overflow property set, the relative value translates into fixed.    */
  /* Ex: parent element DIV with a class of tableContainer has an overflow property set to auto */
  .creativeUnitTable thead.fixedHeader tr {
    position: relative
  }

  /* set THEAD element to have block level attributes. All other non-IE browsers            */
  /* this enables overflow to work on TBODY element. All other non-IE, non-Mozilla browsers */
  html>body .creativeUnitTable thead.fixedHeader tr {
    display: block
  }

  /* make the TH elements pretty */
  .creativeUnitTable thead.fixedHeader th {
    background: #000000;
    color: white;
  }

  /* make the A elements pretty. makes for nice clickable headers                */
  .creativeUnitTable thead.fixedHeader a, .creativeUnitTable thead.fixedHeader a:link, .creativeUnitTable thead.fixedHeader a:visited {
    color: #FFF;
    display: block;
    text-decoration: none;
    width: 100%
  }

  /* make the A elements pretty. makes for nice clickable headers                */
  /* WARNING: swapping the background on hover may cause problems in WinIE 6.x   */
  .creativeUnitTable thead.fixedHeader a:hover {
    color: #FFF;
    display: block;
    text-decoration: underline;
    width: 100%
  }

  /* define the table content to be scrollable                                              */
  /* set TBODY element to have block level attributes. All other non-IE browsers            */
  /* this enables overflow to work on TBODY element. All other non-IE, non-Mozilla browsers */
  /* induced side effect is that child TDs no longer accept width: auto                     */
  html>body .creativeUnitTable tbody.scrollContent {
    display: block;
    max-height:calc(80vh - #{$headerHeight} - 50px);
    overflow: auto;
    width: 100%
  }

  /* Make all the tds and Ths equal widths */
  .creativeUnitTable th, .creativeUnitTable td {
    max-width: $cellWidths;
    min-width: $cellWidths;
  }

   /* Set the first second and third ones to be smaller specific widths */
   //Checkbox
  .creativeUnitTable th:nth-child(1), .creativeUnitTable td:nth-child(1) {
    max-width: 30px; min-width: 30px; padding-left:12px;
  }
  //Buttons
  .creativeUnitTable th:nth-child(2), .creativeUnitTable td:nth-child(2) {
    max-width: 151px; min-width: 151px;
  }
  //Status
  .creativeUnitTable th:nth-child(3), .creativeUnitTable td:nth-child(3) {
    max-width: 100px; min-width: 100px;
  }
  .creativeUnitTable td:nth-child(3) {
    text-align: center;
  }
  //Date
  .creativeUnitTable th:nth-child(4), .creativeUnitTable td:nth-child(4) {
    max-width: 135px; min-width: 135px;
  }
  // Master
  .creativeUnitTable th:nth-child(5), .creativeUnitTable td:nth-child(5) {
    max-width: 150px; min-width: 150px;
  }
  //Locale
  .creativeUnitTable th:nth-child(6), .creativeUnitTable td:nth-child(6) {
    max-width: 200px; min-width: 200px;
  }
  // height and width
  .creativeUnitTable th:nth-child(7), .creativeUnitTable td:nth-child(7), .creativeUnitTable th:nth-child(8), .creativeUnitTable td:nth-child(8) {
    max-width: 90px; min-width: 90px;
  }

  /* make TD elements pretty. Provide alternating classes for striping the table */
  /* http://www.alistapart.com/articles/zebratables/                             */
  .creativeUnitTable tbody.scrollContent td, .creativeUnitTable tbody.scrollContent tr.normalRow td {
    border-bottom: none;
    border-left: none;
  }

  /* ENSURE Contents waraps in TD */
  .creativeUnitTable td {
    word-wrap:break-word;
  }
</style>



// WEBPACK FOOTER //
// src/components/CreativeUnitTable/creative-unit-table.vue